import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Target, Calendar, GraduationCap, Heart, CheckCircle } from "lucide-react";

const WhyUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const reasons = [
    {
      icon: Target,
      title: "Wysoka zdawalność",
      description:
        "Większość naszych kursantów zdaje egzamin za pierwszym razem. To efekt solidnego przygotowania i znajomości tras egzaminacyjnych.",
    },
    {
      icon: Calendar,
      title: "Elastyczne terminy",
      description:
        "Dopasowujemy harmonogram jazd do Twojego planu dnia. Jeździmy również w weekendy i godzinach popołudniowych.",
    },
    {
      icon: GraduationCap,
      title: "Profesjonalne przygotowanie",
      description:
        "Kompleksowe szkolenie teoretyczne i praktyczne. Poznasz wszystkie trasy egzaminacyjne w Nowym Targu.",
    },
    {
      icon: Heart,
      title: "Przyjazna atmosfera",
      description:
        "Cierpliwość i spokój to nasza dewiza. U nas każdy kursant czuje się komfortowo i pewnie za kierownicą.",
    },
  ];

  const benefits = [
    "Egzamin w MORD Nowy Targ",
    "Nowoczesne samochody szkoleniowe",
    "Materiały do nauki teorii w cenie",
    "Pomoc w zapisaniu na egzamin",
    "Cierpliwy i doświadczony instruktor",
    "Konkurencyjne ceny",
  ];

  return (
    <section
      id="dlaczego-my"
      className="py-20 lg:py-32 bg-slate-900"
      data-testid="why-us-section"
      ref={ref}
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Side - Main Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block bg-yellow-400 text-slate-900 px-4 py-2 rounded-sm font-heading font-bold text-sm tracking-wider mb-6">
                DLACZEGO MY?
              </div>
              <h2
                className="font-heading font-bold text-4xl lg:text-5xl text-white tracking-tight mb-6"
                data-testid="why-us-title"
              >
                WYBIERZ SPRAWDZONĄ
                <span className="block text-yellow-400">SZKOŁĘ JAZDY</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-10">
                Dzięki wieloletniemu doświadczeniu i sprawdzonym metodom szkolenia, przygotowujemy 
                kursantów tak, by czuli się pewnie zarówno podczas egzaminu, jak i na co dzień za kierownicą.
              </p>
            </motion.div>

            {/* Benefits List */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
              data-testid="why-us-benefits"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{benefit}</span>
                </div>
              ))}
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-12 relative"
            >
              <img
                src="https://images.unsplash.com/photo-1642927492894-3b738e460dcf?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Ręce na kierownicy"
                className="w-full h-64 object-cover rounded-sm"
                data-testid="why-us-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
            </motion.div>
          </div>

          {/* Right Side - Reason Cards */}
          <div className="space-y-6" data-testid="why-us-reasons">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, x: 30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                className="bg-slate-800/50 border border-slate-700 p-6 rounded-sm hover:border-yellow-400/50 transition-colors group"
                data-testid={`why-us-card-${index}`}
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-yellow-400 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <reason.icon className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-white tracking-tight mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed">{reason.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
